import { Wallet, CreditCard, Landmark, BadgeEuro } from 'lucide-react';

import { type IPaymentMethod } from 'src/graphql/schema';
import { ImageVisa, ImageMasterCard } from 'src/components/Svg/Images';

type CardType = 'visa' | 'mastercard';

type Props = {
  paymentMethod?: IPaymentMethod | null;
};

const cardImageMap: Record<CardType, typeof ImageVisa | typeof ImageMasterCard> = {
  visa: ImageVisa,
  mastercard: ImageMasterCard,
};

const CardPaymentMethod = ({ cardType, cardLast4 }: { cardType: string | null | undefined; cardLast4: string }) => {
  const normalizedCardType = cardType?.toLowerCase();
  const CardIcon =
    normalizedCardType && normalizedCardType in cardImageMap ? cardImageMap[normalizedCardType as CardType] : undefined;

  return (
    <div className="flex items-center gap-2">
      {CardIcon ? (
        <div className="w-9 h-6 border border-border rounded-sm p-0.5">
          <CardIcon className="w-full h-full" data-testid={cardType} />
        </div>
      ) : (
        <CreditCard className="size-6" />
      )}
      <span className="text-base text-foreground font-semibold">xxxx - {cardLast4}</span>
    </div>
  );
};

const BankTransferPaymentMethod = () => (
  <div className="flex gap-2 items-center">
    <Landmark className="size-6" />
    <div className="text-sm text-foreground font-semibold">Bank transfer</div>
  </div>
);

const SepaPaymentMethod = () => (
  <div className="flex gap-2 items-center">
    <BadgeEuro className="size-6" />
    <div className="text-sm text-foreground font-semibold">SEPA</div>
  </div>
);

const NoPaymentMethod = () => (
  <div className="flex items-center gap-2 text-foreground">
    <Wallet className="size-6" />
    <span className="text-sm text-foreground">No Payment Method added</span>
  </div>
);

export default function BillingPayment({ paymentMethod }: Props) {
  if (!paymentMethod) return <NoPaymentMethod />;

  if (paymentMethod.type === 'card' && paymentMethod.cardLast4)
    return <CardPaymentMethod cardType={paymentMethod.cardType} cardLast4={paymentMethod.cardLast4} />;

  if (paymentMethod.type === 'sepa_instant_transfer') return <SepaPaymentMethod />;

  if (!paymentMethod.cardLast4) return <BankTransferPaymentMethod />;

  return null;
}
